<template>
  <div class="deck-box" data-cy="deck-list">
    <component
      :is="linkType"
      :to="getDeckLink(deck)"
      :href="getDeckLink(deck)"
      v-for="deck in decks"
      :target="linkTarget"
      :key="deck.id"
      @click="clickEvent(deck)"
      :class="{highlight: deck.id === highlightDeck, first: $parent.deckCount === 1}"
      class="deck-card"
      data-cy="deck-card"
    >
      <b-tooltip id="deck-tooltip" v-if="$parent.deckCount === 1 && deck.id === highlightDeck" position="is-right" class="large-tooltip" multilined always>
        <template v-slot:content>
          <p class="title is-4">Here's your brand new<br> slide deck 🙌</p>
          <p class="subtitle is-5">Open the deck by clicking on it, and from there you can make changes and launch an event (try it!)</p>
        </template>
      </b-tooltip>
      <DeckThumb :deck="deck">
        <div v-if="false" :class="isPinned(deck.id) || isFavorite(deck.id) ? 'selected' : ''" class="fav-or-pin">
          <template v-if="isOwnerView">
            <i v-if="isPinned(deck.id)" class="selected action-pin fas fa-star" />
            <i v-else class="action-pin far fa-star" />
          </template>
          <template v-else>
            <i v-if="isFavorite(deck.id)" class="selected action-fav fas fa-heart" />
            <i v-else class="action-fav far fa-heart" />
          </template>
        </div>
      </DeckThumb>
      <div v-if="hasDeckEmoji(deck.slides)" class="deck-icon">
        <img :src="getJoinSlide(deck.slides).content.image">
      </div>
      <div v-if="user.id && isOwnerView" class="tag-box">
        <div v-if="deckView === 'user' && isSharedWithTeam(deck)" class="tag is-secondary share-tag">
          <i class="fas fa-user-friends m-r-5" /> shared
        </div>
        <template v-else>
          <div v-if="!canEditDeck(deck)" class="tag is-warning share-tag">
            <i class="fas fa-lock m-r-5" /> view only
          </div>
          <div v-if="!canCopyDeck(deck)" class="tag is-gray share-tag">
            <i class="fas fa-lock m-r-5" /> not copyable
          </div>
        </template>
      </div>
      <div class="deck-content">
        <p v-if="deck.metaData && deck.metaData.vibe" class="vibe">{{ deck.metaData.vibe }}</p>
        <p v-else class="vibe">&nbsp;</p>
        <p class="subtitle is-5 m-t-15 deck-name" data-cy="deck-name">{{ deck.name }}</p>

        <div v-if="isOwnerView" class="level meta">
          <div class="level-left">
            <div class="square">
              <div class="streamline">
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 14 14"><defs><style>.a{fill:none;stroke:#000;stroke-linecap:round;stroke-linejoin:round;}</style></defs><title>image-picture-gallery</title><rect class="a" x="3" y="4" width="10.5" height="8.5" rx="1" transform="translate(16.5 16.5) rotate(180)" /><path class="a" d="M.5,10V2.5a1,1,0,0,1,1-1h9" /><path class="a" d="M3.605,12.419,7.527,8.265a1,1,0,0,1,1.45,0l3.958,4.14" /></svg>
              </div>
              <p>{{ deck.slideCount }} slides</p>
            </div>
            <!-- <div v-if="isTeamDeck(deck)" class="tag m-l-10 is-secondary is-light"> <i class="fas fa-user-friends m-r-5" /> Team Template</div> -->
            <div v-if="!isOwnerView && isSharedWithTeam(deck)" class="tag m-l-10 is-warning is-light"> <i class="fas fa-user-friends m-r-5" /> Your deck</div>
          </div>
          <div v-if="isFullyLoaded" @click.stop.prevent class="buttons are-small deck-actions">
            <!-- <div class="button">Start Event</div> -->
            <n-link v-if="isOwnerDeck(deck)" :to="`/edit/${deck.id}`" class="button">
              Open
            </n-link>
            <div v-if="!isOwnerDeck(deck)" class="buttons">
              <div v-if="canCopyDeck(deck)" @click="duplicateDeck(deck.id)" class="button"><i class="action-fav far fa-clone" /></div>
              <div v-if="!canEditDeck(deck)" @click="clickEvent(deck)" class="button">Preview</div>
              <div v-if="canEditDeck(deck)" @click="goToEdit(deck)" class="button">Open</div>
            </div>
            <b-dropdown v-if="!isTeamDeck(deck)" position="is-top-left" type="is-primary" class="deck-actions" data-cy="deck-actions">
              <template #trigger="{ active }">
                <div class="button">
                  <i v-if="active" class="action-fav fas fa-times" />
                  <i v-else class="action-fav fas fa-ellipsis-h" />
                </div>
              </template>
              <b-dropdown-item @click="duplicateDeck(deck.id)">
                <p><i class="far fa-clone m-r-10" /> Duplicate deck</p>
              </b-dropdown-item>
              <b-dropdown-item @click="startDeleteDeck(deck.id)">
                <p>
                  <i class="far fa-trash-alt m-r-10" data-cy="delete-deck" /> Delete deck
                </p>
              </b-dropdown-item>
              <template v-if="isOwnerDeck(deck) && user?.team?.id">
                <b-dropdown-item v-if="!deck.teamId" @click="openTeamShareModal(deck)">
                  <p><i class="fas fa-user-plus m-r-10" /> Share with team</p>
                </b-dropdown-item>
                <b-dropdown-item v-if="deck.teamId" @click="makeDeckPrivate(deck.id)">
                  <p><i class="fas fa-user-minus m-r-10" /> Stop sharing with team</p>
                </b-dropdown-item>
                <b-dropdown-item v-if="deck.teamId" @click="openTeamShareModal(deck)">
                  <p><i class="fas fa-user-cog m-r-10" /> Edit sharing settings</p>
                </b-dropdown-item>
                <b-dropdown-item v-if="deck.teamId" @click="showOtherVersions(deck)">
                  <p><i class="fas fa-undo-alt m-r-10" /> Restore previous version</p>
                </b-dropdown-item>
              </template>
              <b-dropdown-item @click="openDeckShareModal(deck)">
                <p><i class="fas fa-external-link-alt m-r-10" /> Share public link</p>
              </b-dropdown-item>
            </b-dropdown>
          </div>
        </div>
        <div v-if="!isOwnerView" class="level meta">
          <div class="level-left">
            <div class="square m-r-15">
              <div class="streamline">
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 14 14"><defs><style>.a{fill:none;stroke:#000;stroke-linecap:round;stroke-linejoin:round;}</style></defs><title>image-picture-gallery</title><rect class="a" x="3" y="4" width="10.5" height="8.5" rx="1" transform="translate(16.5 16.5) rotate(180)" /><path class="a" d="M.5,10V2.5a1,1,0,0,1,1-1h9" /><path class="a" d="M3.605,12.419,7.527,8.265a1,1,0,0,1,1.45,0l3.958,4.14" /></svg>
              </div>
              <p>{{ deck.slideCount }} slides</p>
            </div>
            <div class="square m-r-15">
              <div class="streamline">
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 14 14"><defs><style>.a{fill:none;stroke:#000;stroke-linecap:round;stroke-linejoin:round;}</style></defs><title>interface-time-clock-circle</title><circle class="a" cx="7" cy="7" r="6.5" /><polyline class="a" points="7 4.5 7 7 9.541 9.965" /></svg>
              </div>
              <p>{{ durationEstimate(deck.slideCount) }}</p>
            </div>
            <div v-if="deck.metaData && deck.metaData.groupSize" class="square">
              <div class="streamline">
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 14 14"><defs><style>.a{fill:none;stroke:#000;stroke-linecap:round;stroke-linejoin:round;}</style></defs><title>interface-user-multiple</title><circle class="a" cx="5" cy="3.25" r="2.25" /><path class="a" d="M9.5,13H.5V11.5a4.5,4.5,0,0,1,9,0Z" /><path class="a" d="M8.48,1.06A2.284,2.284,0,0,1,9,1,2.25,2.25,0,0,1,9,5.5a2.292,2.292,0,0,1-.48-.051" /><path class="a" d="M9.976,7.106A4.5,4.5,0,0,1,13.5,11.5V13h-2" /></svg>
              </div>
              <p>{{ groupSize(deck.metaData) }} people</p>
            </div>
          </div>
        </div>
      </div>
    </component>
    <b-modal v-model="showOtherVersionsModal" @close="clearOtherVersionsModal()" :width="550" class="versions-modal">
      <div class="card">
        <p class="title is-3">Previous versions</p>
        <div v-for="deck in otherDeckVersions" :key="deck.id + '-version'" class="deck-version m-b-20">
          <p class="is-size-5">
            {{ deck.name }}
            <span v-if="deck.id === deck.teamData.originalVersion" class="tag is-warning m-l-5">original</span>
          </p>
          <p class="is-size-6">{{ format(parseISO(deck.dateCreated), "LLL do, h:mmaaaaaa") }}</p>
          <div class="buttons is-right m-t-20">
            <b-tooltip label="Permanently delete this version, cannot be undone." position="is-top" type="is-dark" multilined>
              <div @click="startDeleteDeck(deck.id)" class="button is-small"><i class="fas fa-trash-alt" style="color: #444" /></div>
            </b-tooltip>
            <b-tooltip label="Replace the current deck in your account with this version" position="is-top" type="is-dark" multilined>
              <div @click="replaceWithVersion(deck.id)" class="button is-small">Replace existing</div>
            </b-tooltip>
            <b-tooltip label="Copy this version into your account as a new deck" position="is-top" type="is-dark" multilined>
              <div @click="duplicateDeck(deck.id); clearOtherVersionsModal()" class="button is-small is-primary">Restore a copy</div>
            </b-tooltip>
          </div>
        </div>
      </div>
    </b-modal>
  </div>
</template>

<script>
import { parseISO, format } from 'date-fns'

import DeckThumb from '~/components/DeckThumb'
import DeckShareModal from '~/components/modals/DeckShare'

import selectDeckForCopy from '~/queries/selectDeckForCopy.gql'
import insertDeckCopy from '~/queries/insertDeckCopy.gql'
import deleteSlideDeck from '~/queries/deleteSlideDeck.gql'
import { updateTeamData, makeDeckPrivate, addDeckToTeam } from '~/queries/teamControls.gql'

import TeamDeckModal from '~/components/modals/TeamDeckModal.vue'

export default {
  auth: false,
  components: {
    DeckThumb
  },
  props: {
    decks: {
      type: Array,
      default: () => { return [] },
      required: true
    },
    isOwnerView: {
      type: Boolean,
      default: false,
      required: false
    },
    showNewDeck: {
      type: Boolean,
      default: false,
      required: false
    },
    deckLink: {
      type: String,
      default: null
    },
    linkTarget: {
      type: String,
      default: null
    },
    linkType: {
      type: String,
      default: 'n-link'
    },
    highlightDeck: {
      type: Number,
      default: 0
    },
    deckView: {
      type: String,
      default: 'user'
    }
  },
  data () {
    return {
      showOtherVersionsModal: false,
      otherVersionsDeckId: null,
      otherVersionsOriginalId: null
    }
  },
  computed: {
    isFullyLoaded () {
      return this.$store.state.user && this.$store.state.user.id
    },
    user () {
      return this.$store.state.user
    },
    otherDeckVersions () {
      const id = this.otherVersionsDeckId
      const origId = this.otherVersionsOriginalId
      const allDecks = this.$parent.slideDecks || []
      return allDecks.filter(deck => deck.id !== id && deck.teamData?.originalVersion === origId)
    }
  },
  methods: {
    format (date, fmt) {
      return format(date, fmt)
    },
    parseISO (date) {
      return parseISO(date)
    },
    isOwnerDeck (deck) {
      return this.user.id === deck.userId
    },
    isTeamDeck (deck) {
      const isOwner = this.user.id === deck.userId
      return !isOwner && deck.teamId?.length > 10
    },
    canEditDeck (deck) {
      const canEditTeamDeck = this.deckView === 'team' && deck.teamData?.teamId &&
                              deck.teamData.teamId === this.user?.team?.id &&
                              deck.teamData?.allowEdit !== false &&
                              this.user.team?.canEdit !== false

      return this.isOwnerDeck(deck) || canEditTeamDeck
    },
    canCopyDeck (deck) {
      const isOwner = this.user.id === deck.userId
      const canCopyTeamDeck = deck.teamData?.teamId && // is team deck
                              deck.teamData?.allowCopy !== false &&
                              deck.teamData?.teamId === this.user?.team?.id

      return isOwner || deck.isPublic || canCopyTeamDeck
    },
    isSharedWithTeam (deck) {
      const isOwner = this.user.id === deck.userId
      return isOwner && deck.teamId?.length > 10
    },
    getDeckLink (deck) {
      if (!this.isOwnerView) { return '/preview/' + deck.id }

      const isOwner = this.isOwnerDeck(deck)
      const canEdit = this.canEditDeck(deck)
      if (isOwner || canEdit) {
        return '/edit/' + deck.id
      } else {
        return '/preview/' + deck.id
      }
    },
    getJoinSlide (slides) {
      return slides.find(slide => slide.template === 'joinSlide') || {}
    },
    hasDeckEmoji (slides) {
      return this.getJoinSlide(slides).content && this.getJoinSlide(slides).content.image
    },
    getPrimaryColor (theme) {
      return theme.colors.colors.accent
    },
    clickEvent (deck) {
      this.$emit('deck:click', deck)
    },
    goToEdit (deck) {
      if (!this.canEditDeck(deck)) {
        this.$buefy.toast.open({ type: 'is-warning', message: `Owner doesn't allow editing this deck` })
        return
      }

      this.$router.push({ path: '/edit/' + deck.id })
    },
    isFavorite (deckId) {
      return false
    },
    isPinned (deckId) {
      return false
    },
    durationEstimate (slideCount) {
      const base = 5 * Math.round(slideCount / 5)

      let margin = Math.round(base / 25)
      margin = margin * 5

      if (margin === 0) {
        return base + '-' + (base + 5) + ' mins'
      }
      return (base - margin) + '-' + (base + margin) + ' mins'
    },
    groupSize (metaData) {
      if (!metaData) { return '' }

      const groups = {
        small: '3-10',
        medium: '10-50',
        large: '30-500',
        all: '3-500'
      }
      return groups[metaData.groupSize] || '3-10'
    },
    openDeckShareModal (deck) {
      this.$buefy.modal.open({
        parent: this,
        fullScreen: false,
        component: DeckShareModal,
        width: 500,
        customClass: 'deck-share-modal',
        trapFocus: true,
        events: {
          'refresh:decks': () => { this.$parent.refreshDeckQueries() }
        },
        props: {
          deckId: deck.id,
          ownerId: deck.userId,
          isPublic: deck.isPublic
        }
      })
    },
    showOtherVersions (deck) {
      if (!deck.id || !deck.teamData || !deck.teamData.originalVersion) {
        this.$buefy.toast.open({ type: 'is-warning', message: 'No previous versions found' })
        return
      }
      this.otherVersionsDeckId = deck.id
      this.otherVersionsOriginalId = deck.teamData.originalVersion
      this.showOtherVersionsModal = true
      this.selectedDeck = deck
    },
    clearOtherVersionsModal () {
      this.otherVersionsDeckId = null
      this.otherVersionsOriginalId = null
      this.showOtherVersionsModal = false
      this.selectedDeck = null
    },
    startDeleteDeck (deckId) {
      this.$buefy.dialog.confirm({
        title: 'Delete this Slide Deck?',
        message: 'Are you sure you want to permanently delete this slide deck? All data will be lost and this action can\'t be undone.',
        cancelText: 'Cancel',
        class: 'delete-deck',
        confirmText: 'Delete This Deck',
        onConfirm: () => { this.deleteDeck(deckId) }
      })
    },
    async deleteDeck (deckId) {
      const deck = this.decks.filter(d => d.id === deckId)[0]

      this.$parent.changingDeck = true
      await this.$apollo.mutate({
        mutation: deleteSlideDeck,
        variables: {
          id: deckId
        }
      })

      await this.$parent.refreshDeckQueries()
      this.$buefy.toast.open({ type: 'is-success', message: 'Slide deck deleted...' })

      this.posthogCapture('deck:delete', { deck_id: deckId, deck_name: deck.name })
    },
    openTeamShareModal (deck) {
      this.log('opening team share modal for deck: ' + deck.id)
      this.$buefy.modal.open({
        parent: this,
        component: TeamDeckModal,
        trapFocus: true,
        width: 450,
        events: {
        },
        props: {
          deck
        }
      })
    },
    async makeDeckPrivate (deckId) {
      this.$parent.changingDeck = true
      await this.$apollo.mutate({
        mutation: makeDeckPrivate,
        variables: {
          id: deckId
        }
      })

      await this.$parent.refreshDeckQueries()
      this.$buefy.toast.open({ type: 'is-success', message: 'Slide deck is now private' })
    },
    startDuplicateDeck () {
      const deckId = this.$route.query.startDeck
      this.$router.replace({ path: '/decks', query: {} })
      this.duplicateDeck(deckId)
    },
    async duplicateDeck (deckId, options = { refreshQueries: true, keepName: false }) {
      this.log('duplicating deck from DeckList.vue with options: ', options)

      this.$parent.changingDeck = true
      // this.$buefy.snackbar.open({ type: 'is-primary', message: 'Copying slide deck...', queue: false })
      const { data: { slideDecks } } = await this.$apollo.query({
        query: selectDeckForCopy,
        variables: {
          id: deckId
        }
      })

      const deck = slideDecks[0]

      if (!deck || !deck.slides || deck.slides.length < 1) {
        this.throwError({ message: 'Cannot duplicate: Deck not found or not public' }, 'toast')
        return
      }

      if (!this.canCopyDeck(deck)) {
        this.log(`can't copy deck: `, deck.userId, this.user.id)
        this.$buefy.toast.open({ type: 'is-warning', message: 'Owner does not allow copying this deck' })
        return
      }

      let slides = [ ...deck.slides ]
      const isProUser = this.$store.getters.isProUser

      slides = slides.map((slide) => {
        // remove hidden logos when deck is copied by non-pro users
        if (!isProUser) { slide.options.hideLogoThisSlide = false }

        return {
          options: slide.options,
          content: slide.content,
          prompts: slide.prompts,
          template: slide.template,
          order: slide.order,
          userId: this.user.id,
          answers: slide.answers,
          steps: slide.steps
        }
      })

      // clear custom logos & branding for non-pro users
      if (!isProUser) {
        deck.metaData.showModeratorBrand = true
        deck.metaData.showPlayerBrand = true
      }

      // if current user owns deck, copy settings
      const teamData = {}
      if (deck.userId === this.user.id && deck.teamData) {
        teamData.lockDeckSettings = deck.teamData.lockDeckSettings || false
        teamData.allowCopy = deck.teamData.allowCopy || true
        teamData.allowEdit = deck.teamData.allowEdit || true
        // teamData.isVisible = deck.teamData.isVisible || true
        // teamData.visibleToUsers = deck.teamData.visibleToUsers || []
      }

      // if current user owns deck, copy eventSettings
      let eventSettings = {}
      if (deck.userId === this.user.id && deck.eventSettings) {
        eventSettings = deck.eventSettings
        if (!isProUser) { delete eventSettings.collectPlayerEmails }
        if (eventSettings.shortcode) { delete eventSettings.shortcode } // don't bring join codes with copies
      }

      // eslint-disable-next-line camelcase
      const { data: { insert_slideDecks } } = await this.$apollo.mutate({
        mutation: insertDeckCopy,
        variables: {
          name: options.keepName ? deck.name : 'Copy of ' + deck.name,
          themeData: deck.themeData,
          metaData: deck.metaData,
          copyFromDeckId: deckId,
          userId: this.user.id,
          slides: { data: slides },
          isPublic: deck.isPublic,
          teamData,
          eventSettings
        }
      })

      // eslint-disable-next-line camelcase
      const newDeck = insert_slideDecks
      if (!newDeck || !newDeck.returning || !newDeck.returning[0]) {
        this.throwError({ message: 'Error creating new slide deck' }, 'toast')
      }

      if (options.refreshQueries !== false) {
        await this.$parent.refreshDeckQueries()
      }

      if (this.deckView === 'team') {
        this.$buefy.toast.open({ type: 'is-success', message: 'Copied! Go to "My Decks" to view' })
      }

      this.posthogCapture('deck:copy', { from_deck_id: deckId, from_deck_name: deck.name })

      const newId = newDeck.returning[0].id
      this.$parent.highlightDeck = newId
      return newId
    },
    async updateTeamData (deckId, teamData) {
      await this.$apollo.mutate({
        mutation: updateTeamData,
        variables: {
          id: deckId,
          teamData
        }
      })
    },
    async addDeckToTeam (deckId, teamId, teamData) {
      await this.$apollo.mutate({
        mutation: addDeckToTeam,
        variables: {
          id: deckId,
          teamId,
          teamData
        }
      })
    },
    async replaceWithVersion (versionId) {
      // duplicate version to replace current deck
      const newId = await this.duplicateDeck(versionId)
      if (!newId) { this.log('no new deck id'); return }

      // update teamData of the newly duplicated deck to match the previously current deck
      const teamData = this.selectedDeck.teamData
      delete teamData.isArchived
      await this.addDeckToTeam(newId, teamData.teamId, teamData)

      // update teamData and id of the current deck to be archived
      teamData.isArchived = true
      await this.updateTeamData(this.selectedDeck.id, teamData)
      await this.makeDeckPrivate(this.selectedDeck.id)

      this.clearOtherVersionsModal()
      await this.$parent.refreshDeckQueries()
      this.$buefy.toast.open({ type: 'is-success', message: 'Version successfully restored' })
    }
  }
}
</script>
<style lang="scss">
.b-tooltip.large-tooltip.is-multiline {

  .tooltip-content {
    width: 370px;
    left: calc(100% + 10px + 2px);
    padding: 1.7em 1.9em;
    border-radius: 10px;

    p {
      color: white;
    }

    &:before {
      border-top: 10px solid transparent;
      border-right: 10px solid #E20761;
      border-bottom: 10px solid transparent;
    }
  }
}
.deck-actions {
  .dropdown-menu {
    min-width: 6em;
  }
  .dropdown-content {
    margin-bottom: 4px;

    i {
      width: 1.2em;
    }
  }
}

.versions-modal {
  .modal-content {
    overflow: visible;
  }

  .card {
    padding: 1.5em 2em;

    .deck-version {
      padding: .75em .5em .75em 1em;
      border: 1px solid #eee;
      border-radius: 4px;
      position: relative;
    }

    .tag {
      //position: absolute;
      //right: 1em;
      //top: 1em;
      background-color: #ffe9ad;
    }
  }
}
</style>
<style lang="scss" scoped>
.buttons.are-small.deck-actions{
  .button {
    font-size: .75em !important;
  }
}
.tag {
  &.is-secondary.is-light {
    color: var(--secondary);
    background-color: #e5f0f9;
  }

    &.is-warning.is-light {
    color: #785f00;
    background-color: #f9eabf;
  }
}

.deck-box {
  margin-left: 30px;
  margin-right: 30px;
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  @media(max-width: 1216px) {
    grid-template-columns: 1fr 1fr;
  }
  @media(max-width: 780px) {
    grid-template-columns: 1fr;
    justify-content: center;
  }
  .deck-card {
    width: 100%;
  }

  &.no-margin {
    margin-left: 0;
    margin-right: 0;
  }
  .tag-box {
    position: absolute;
    top: 20px;
    left: 24px;

    .share-tag {
      margin-right: 4px;
    }
  }
}
.deck-name {
  min-height: 2.5em;
}

.highlight {
  &.deck-card {
    z-index: 2;
    box-shadow:  1px 8px 30px -8px rgba(0, 0, 0, 0.3);

    &.first{
      box-shadow: 0 0 0 2000px rgba(0,0,0,.3),
      1px 8px 50px -8px rgba(0, 0, 0, 0.4);
      z-index: 10;
    }
  }

  #deck-tooltip {
    position: absolute;
    right: 0;
    top: 50%;
  }
}

.deck-thumb {
  // box-shadow: 1px 8px 16px -8px rgba(0,0,0,.1);
  box-shadow: 0px 2px 10px -3px rgba(0,0,0,.1);
}

.deck-card {
  border: 1px solid rgb(217, 223, 226);
  border: 1px solid rgba(229, 234, 236, 0.712);
  padding: .8em;
  position: relative;
  transition: box-shadow .16s ease;

  &:hover {
    // transform: scale(1);
    box-shadow: 0 0.85rem 1.5rem rgba(18, 38, 63, 0.08);
    border: 1px solid rgba(229, 234, 236, 0.9);

    .fav-or-pin, .fav-or-pin.selected {
      opacity: 1;
    }
  }

  .bottom-wave-2 {
    display: none;
    z-index: 5;
    position: absolute;
    width: 100%;
    bottom: -4px;
    left:0px;
    right:0;
    // transform: rotate(1deg) scaleY(.9);
  }

  .deck-slot {
    z-index: 5;
    position: absolute;
    top: 0; bottom: 0; left: 0; right: 0;
    display: flex;
    justify-content: center;
    align-items: center;
    padding: 0 2em;
    text-align: center;
  }

  .deck-title {
      font-size: 1.5em;
      margin: 0;
      line-height: 1.4em;
      text-align: left;
      margin-left: .5em;
    }

    .deck-title-icon {
      width: 3em;
      height: 3em;
    }
  .fav-or-pin {
    position: absolute;
    top: .5em;
    right: .5em;
    z-index: 2;
    opacity: 0;
    transition: opacity .2s           ease;

    &.selected {
      opacity: .4;
    }

    .action-fav {
      font-size: 1.1em;
      opacity: .5;
      color: var(--fullContrast);
      transition: color .13s ease, opacity .13s ease;
      margin-right: 1px;

      &:hover {
        opacity: 1;
      }

      &.selected {
        opacity: 1;
      }
    }

    .action-pin {
      opacity: .4;
      color: var(--fullContrast);
      font-size: 1.1em;
      transition: color .13s ease;
      transition: color .13s ease, opacity .13s ease;

      &:hover {
        opacity: 1;
      }

      &.selected {
        opacity: 1;
      }
    }
  }

  .deck-icon {
    // display: none;
    position: absolute;
    top: 115px;
    right: 9px;
    // left: 0;
    margin: 0;
    display: block;
    opacity: 1;
    height: 85px;
    width: 85px;
    z-index: 5;
    background-color: #fff;
    // border: 1px solid #eee;
    border-radius: 50px;
    padding: 16px;
    // box-shadow: 1px 6px 12px -8px rgba(0,0,0,.2);
    text-align: center;

    img {
      max-height: 100%;
      height: 100%;
      margin: 0 auto;
    }
  }

  .deck-content{
    padding-bottom: .6em;

    .vibe {
      text-transform: uppercase;
      font-size: .7em;
      color: #888;
      margin-bottom: -15px;
      margin-top: 10px;
      text-align: left;
    }

    .subtitle {
      padding-right: 100px;
      text-align: left;
      margin-bottom: .75em;
    }
    .level.meta {
      color: #444;

      .streamline {
        height: 16px;
        width: 16px;
        top: -1px;
        position: relative;

        .a {
          stroke: currentColor;
        }
      }

      .square {
        display: flex;

        p {
          margin-left: 4px;
          font-size: 14px;
        }
      }
    }
  }
}
</style>
